import { api } from '@api/v1/client'
import { requireArray } from '@api/v1/readShape'
import { stripHtml } from './reportGridUtils'

/**
 * CC-53890 (spec 128) — the option list behind every ACCOUNTSDROPDOWN filter.
 *
 * One builder for the account picker's options, used by `DropdownFilter` (the
 * control) AND by the General Ledger page (its stale-selection check and its
 * export masthead). Two builders would let the name a user ticked and the name
 * the export prints drift apart on a financial statement.
 *
 * ## What an option carries
 *
 *   key       the CB account id as a string — the wire value, never reformatted
 *   value     the LABEL the user reads (below)
 *   group     CB's account TYPE name (Bank, Credit Card, Expense…) — V1 parity
 *   search    what the search box matches: number, name AND type group, so
 *             "1000", "morgan" and "bank" all find JP Morgan Operating
 *   parentKey the parent account's id, from `parentId`. CB's `IsParent` flag is
 *             false on all 109 of vladdemo's accounts (measured), so parenthood
 *             is derived from children pointing at a parent, never from the flag.
 *   depth     nesting depth under its parent, for the tree indent
 *   inactive  CB's IsInactive
 *
 * ## The label, and why it has so many rules
 *
 * `number · name`, where number is the CHART number. Measured on vladdemo:
 *   - 3 duplicate-NAME pairs (Accounts Payable -17/164, Accounts Receivable
 *     -16/163, Filing Fees 180/186) and 3 duplicate-NUMBER pairs (10101, 6025,
 *     6000.002.004.003). Name + number separates the number pairs; the parent's
 *     name separates Filing Fees; nothing separates the AP/AR pairs except that
 *     one of each is a negative-id SYSTEM account. A pair that still collides
 *     after all of that shows its id — the only thing left that tells them
 *     apart — rather than two identical rows a user must guess between.
 *   - CB sends names HTML-encoded ("Mediator &amp; Arbitrator Fees"); they are
 *     decoded here because React escapes text itself.
 *
 * The number is `centerbaseAccountNumber`, NEVER `accountNumber`: cb-api's
 * `accountNumber` falls back to a Bank account's number AT THE BANK when the
 * chart number is missing (accounting/routes.js mapAccount), and printing a bank
 * account number beside a GL account name is both wrong and a disclosure. A
 * worker too old to send the field gives no number at all — a missing number is
 * honest, a bank number is not.
 */

const NUMBER_SEP = ' · '

/** The account's CHART number, or '' — never the bank-account fallback. */
export const chartNumberOf = (a) =>
  a && Object.prototype.hasOwnProperty.call(a, 'centerbaseAccountNumber')
    ? String(a.centerbaseAccountNumber ?? '').trim()
    : ''

const clean = (s) => stripHtml(String(s ?? '')).trim()

const collisions = (labels) => {
  const seen = new Map()
  for (const l of labels) {
    const k = l.toLowerCase()
    seen.set(k, (seen.get(k) || 0) + 1)
  }
  return (l) => (seen.get(l.toLowerCase()) || 0) > 1
}

/**
 * Build picker options from `/accounting/accounts` rows (cb-api's mapped shape).
 *
 * @param {object[]} accounts
 * @param {{ wanted?: Set<number>|null }} [opts] restrict to these ids (a generic
 *   report definition's FilteredAccountIds, CC-49864/65); null = every account
 * @returns {object[]} options in TREE order within CB's own order
 */
export const buildAccountOptions = (accounts, { wanted = null } = {}) => {
  const rows = (accounts || []).filter((a) => a?.id != null && (!wanted || wanted.has(Number(a.id))))
  const base = rows.map((a) => {
    const name = clean(a.name) || `Account ${a.id}`
    const number = chartNumberOf(a)
    return {
      a,
      name,
      number,
      parentName: clean(a.parentName),
      label: number ? `${number}${NUMBER_SEP}${name}` : name,
    }
  })

  // Pass 1: colliding labels take their parent's name.
  let hit = collisions(base.map((b) => b.label))
  for (const b of base) if (hit(b.label) && b.parentName) b.label = `${b.label} (under ${b.parentName})`
  // Pass 2: a negative id is one of CB's system accounts.
  hit = collisions(base.map((b) => b.label))
  for (const b of base) if (hit(b.label) && Number(b.a.id) < 0) b.label = `${b.label} (system account)`
  // Pass 3: whatever still collides shows the one thing left that differs.
  hit = collisions(base.map((b) => b.label))
  for (const b of base) if (hit(b.label)) b.label = `${b.label} (id ${b.a.id})`

  const options = base.map((b) => ({
    key: String(b.a.id),
    value: b.a.isInactive ? `${b.label} (inactive)` : b.label,
    group: clean(b.a.type) || 'Accounts',
    search: [b.number, b.name, clean(b.a.type)].filter(Boolean).join(' '),
    parentKey: b.a.parentId != null && String(b.a.parentId) !== String(b.a.id) ? String(b.a.parentId) : null,
    inactive: !!b.a.isInactive,
    depth: 0,
  }))
  return treeOrder(options)
}

/**
 * Re-order so each sub-account follows its parent (V1's tree), keeping CB's own
 * order among siblings. A parent that is not in the list, or sits in another
 * type group, leaves its children at the top level of their own group. A
 * cycle in the data cannot hang this: every option is emitted exactly once.
 */
const treeOrder = (options) => {
  const byKey = new Map(options.map((o) => [o.key, o]))
  const children = new Map()
  const roots = []
  for (const o of options) {
    const parent = o.parentKey ? byKey.get(o.parentKey) : null
    if (parent && parent.group === o.group) {
      if (!children.has(parent.key)) children.set(parent.key, [])
      children.get(parent.key).push(o)
    } else {
      roots.push(o)
    }
  }
  const out = []
  const done = new Set()
  const visit = (o, depth) => {
    if (done.has(o.key)) return
    done.add(o.key)
    out.push({ ...o, depth })
    for (const c of children.get(o.key) || []) visit(c, depth + 1)
  }
  for (const r of roots) visit(r, 0)
  // Anything only reachable through a cycle.
  for (const o of options) visit(o, 0)
  return out
}

/**
 * parent key → its direct children's keys, from `parentKey`. Build it once and
 * pass it to `descendantsOf` when asking about many keys at once (the picker
 * counts every row's sub-accounts on every render).
 */
export const childrenIndex = (options) => {
  const children = new Map()
  for (const o of options || []) {
    if (!o.parentKey) continue
    if (!children.has(o.parentKey)) children.set(o.parentKey, [])
    children.get(o.parentKey).push(o.key)
  }
  return children
}

/**
 * Every descendant of `key` (children, grandchildren, …), from `parentKey`.
 * Measured: CB matches account ids EXACTLY — picking parent 117 "Fees Received"
 * returns only its own postings, none of its 13 sub-accounts — so a picker that
 * means "this parent" must send the sub-accounts' ids explicitly (as V1 does).
 *
 * @param {Map<string,string[]>} [children] a prebuilt `childrenIndex(options)`
 */
export const descendantsOf = (key, options, children = childrenIndex(options)) => {
  const out = []
  const seen = new Set([String(key)])
  const stack = [...(children.get(String(key)) || [])]
  while (stack.length) {
    const k = stack.pop()
    if (seen.has(k)) continue
    seen.add(k)
    out.push(k)
    stack.push(...(children.get(k) || []))
  }
  return out
}

/** The react-query key the picker and the GL page share, so they share one fetch. */
export const accountOptionsQueryKey = (accountIds) => ['report-filter-accounts', (accountIds || []).join(',')]

/**
 * Load the chart of accounts as picker options.
 *
 * `requireArray`, not `|| []`: a 200 whose body is not the account array is a
 * FAILED read, and on this control an empty account list is not a harmless
 * blank — an empty selection means ALL accounts to CB.
 */
export const fetchAccountOptions = async (accountIds) => {
  const resp = await api.get('/accounting/accounts')
  const wanted = accountIds && accountIds.length > 0 ? new Set(accountIds.map(Number)) : null
  return buildAccountOptions(requireArray(resp?.data, { noun: 'account list', entryNoun: 'account' }), { wanted })
}
